import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Header, { scrollToSection } from "./Header";
import Footer from "./Footer";
import { ARTICLES } from "@/data/articles";
import { textToBlocks, readTime } from "@/lib/articleText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ block, index }) => {
  if (block.type === "h") {
    return (
      <div data-testid={`article-block-${index}`} className="mt-16">
        <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#F2824F]">
          {String(block.num).padStart(2, "0")}
        </span>
        <h2 className="mt-3 font-display text-2xl font-light tracking-tight text-slate-100 sm:text-3xl">
          {block.text}
        </h2>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul data-testid={`article-block-${index}`} className="mt-8 space-y-4">
        {block.items.map((item) => (
          <li key={item} className="flex gap-4">
            <span
              className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#F26122]/70"
              aria-hidden="true"
            />
            <span className="text-base font-light leading-relaxed text-slate-300">
              {item}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "note") {
    return (
      <p
        data-testid={`article-block-${index}`}
        className="mt-14 border-l border-[#F26122]/40 pl-5 text-xs font-light italic leading-relaxed text-slate-500"
      >
        {block.text}
      </p>
    );
  }
  return (
    <p
      data-testid={`article-block-${index}`}
      className="mt-8 text-base font-light leading-relaxed text-slate-300 sm:text-lg"
    >
      {block.text}
    </p>
  );
};

const Article = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`);
        if (!active) return;
        setArticle({
          ...data,
          tag: "Perspective",
          author: "Sharanjit Singh Panesar",
          authorRole: "Founder & Capital Advisor",
          blocks: textToBlocks(data.body || ""),
          readTime: readTime(data.body || ""),
          date: data.created_at
            ? new Date(data.created_at).toLocaleString("en-IN", {
                month: "long",
                year: "numeric",
              })
            : "",
        });
      } catch {
        const fallback = ARTICLES.find((a) => a.slug === slug);
        if (active)
          setArticle(
            fallback
              ? {
                  tag: "Perspective",
                  author: "Sharanjit Singh Panesar",
                  authorRole: "Founder & Capital Advisor",
                  date: "",
                  ...fallback,
                  blocks: fallback.blocks || textToBlocks(fallback.body || ""),
                  readTime: fallback.readTime || readTime(fallback.body || ""),
                }
              : null
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0B0F17]" />;
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0B0F17]">
        <Header />
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center">
          <h1 className="font-display text-3xl font-light text-slate-100">
            This perspective is not available.
          </h1>
          <Link
            data-testid="article-notfound-home-link"
            to="/"
            className="mt-8 border border-[#F26122]/60 px-6 py-3 text-sm text-[#F26122] transition-colors hover:bg-[#F26122] hover:text-[#0B0F17]"
          >
            Return home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  let chapterCount = 0;
  const numberedBlocks = article.blocks.map((b) =>
    b.type === "h" ? { ...b, num: ++chapterCount } : b
  );

  return (    <div className="min-h-screen bg-[#0B0F17]" data-testid="article-page">
      <Seo
        title={`${article.title} | Jeet Advisory Services`}
        siteName="Jeet Advisory Services"
        description={article.description}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          author: {
            "@type": "Person",
            name: article.author,
            jobTitle: article.authorRole,
          },
          publisher: { "@type": "Organization", name: "Jeet Advisory Services" },
          datePublished: "2026-07",
        }}
      />
      <Header />

      <article className="mx-auto max-w-3xl px-4 pb-24 pt-36 sm:px-8 sm:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            data-testid="article-back-link"
            onClick={() => scrollToSection("insights")}
            className="group flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-[#F26122]"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            All insights
          </button>

          <p className="mt-10 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#F2824F]">
            Insights · {article.tag}
          </p>
          <h1
            data-testid="article-title"
            className="mt-6 font-display text-4xl font-light leading-[1.1] tracking-tight text-slate-100 sm:text-5xl"
          >
            {article.title}
          </h1>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
            <span>{article.author}</span>
            <span aria-hidden="true">·</span>
            <span>{article.date}</span>
            <span aria-hidden="true">·</span>
            <span>{article.readTime}</span>
          </div>

          <div className="hairline-gold mt-10 opacity-40" aria-hidden="true" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4"
        >
          {numberedBlocks.map((block, i) => (
            <Block key={i} block={block} index={i} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 border border-white/10 bg-[#0D1220] p-8 sm:p-10"
        >
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#F2824F]">
            Jeet Advisory Services
          </p>
          <h2 className="mt-4 font-display text-2xl font-light text-slate-100 sm:text-3xl">
            Have a capital requirement?
          </h2>
          <p className="mt-4 max-w-lg text-sm font-light leading-relaxed text-slate-400">
            Tell us about your funding requirement and our team will evaluate
            the appropriate capital route.
          </p>
          <button
            data-testid="article-cta-discuss"
            onClick={() => scrollToSection("contact")}
            className="group mt-8 flex items-center gap-2 bg-[#F26122] px-6 py-3.5 text-sm font-medium text-[#0B0F17] transition-colors duration-300 hover:bg-[#FF7B3D]"
          >
            Discuss Your Requirement
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </article>

      <Footer />
    </div>
  );
};

export default Article;
